import React, { useCallback, useMemo, useState } from 'react';
import {
  IconButton,
  IconButtonProps,
} from '@lifeomic/chroma-react/components/IconButton';
import { Copy } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Folder, ModelUtils } from '../../models';
import { clsx } from 'clsx';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { defineMessages, useIntl } from 'react-intl';
import { useCopyTemplatingFolder } from '../../hooks/useCopyTemplatingFolder';

const messages = defineMessages({
  copyFolder: {
    id: 'CopyTemplatingFolderButton.copyFolder',
    defaultMessage: 'Copy folder',
  },
  modalTitle: {
    id: 'CopyTemplatingFolderButton.modalTitle',
    defaultMessage: 'Copy folder',
  },
  folderNameLabel: {
    id: 'CopyTemplatingFolderButton.folderNameLabel',
    defaultMessage: 'New folder name',
  },
  cancelButton: {
    id: 'CopyTemplatingFolderButton.cancelButton',
    defaultMessage: 'Cancel',
  },
  copyButton: {
    id: 'CopyTemplatingFolderButton.copyButton',
    defaultMessage: 'Copy',
  },
  folderNameRequired: {
    id: 'CopyTemplatingFolderButton.folderNameRequired',
    defaultMessage: 'Folder name is required',
  },
  folderNameMaxLength: {
    id: 'CopyTemplatingFolderButton.folderNameMaxLength',
    defaultMessage: 'Folder name cannot exceed 256 characters',
  },
  folderNameInvalidCharacters: {
    id: 'CopyTemplatingFolderButton.folderNameInvalidCharacters',
    defaultMessage:
      'Folder name contains invalid characters. Only letters, numbers, spaces, or special characters ! _ . * ( ) - are allowed.',
  },
});

const useStyles = makeStyles(theme => ({
  copyTemplatingFolderButton: {
    padding: 0,
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

type CopyTemplatingFolderButtonProps = Partial<IconButtonProps> & {
  folder: Folder;
  className?: string;
};

export const CopyTemplatingFolderButton: React.FC<
  CopyTemplatingFolderButtonProps
> = ({ folder, className, ...props }) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const { copyTemplatingFolder, isCopying } = useCopyTemplatingFolder();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [folderName, setFolderName] = useState(
    `${ModelUtils.getName(folder)} (copy)`,
  );

  const errorMessage = useMemo(() => {
    if (folderName.length === 0) {
      return formatMessage(messages.folderNameRequired);
    }
    if (folderName.length > 256) {
      return formatMessage(messages.folderNameMaxLength);
    }
    if (!/^[a-zA-Z0-9!_.*'() -]+$/.test(folderName)) {
      return formatMessage(messages.folderNameInvalidCharacters);
    }
    return undefined;
  }, [folderName, formatMessage]);

  const hasError = !!errorMessage;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setFolderName(`${ModelUtils.getName(folder)} (copy)`);
      setIsModalOpen(true);
    },
    [folder],
  );

  const handleDismiss = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleCopy = useCallback(async () => {
    const isSuccess = await copyTemplatingFolder(folder, folderName.trim());
    if (isSuccess) {
      setIsModalOpen(false);
    }
  }, [copyTemplatingFolder, folder, folderName]);

  return (
    <>
      <IconButton
        {...props}
        size={0}
        icon={Copy}
        disabled={isCopying}
        aria-label={formatMessage(messages.copyFolder)}
        onClick={handleClick}
        className={clsx(className, styles.copyTemplatingFolderButton)}
        color="inverse"
      />
      <Modal
        isOpen={isModalOpen}
        onDismiss={handleDismiss}
        size={1}
        title={formatMessage(messages.modalTitle)}
        actions={
          <>
            <Button variant="text" color="negative" onClick={handleDismiss}>
              {formatMessage(messages.cancelButton)}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleCopy()}
              disabled={hasError || isCopying}
            >
              {formatMessage(messages.copyButton)}
            </Button>
          </>
        }
      >
        <div className={styles.modalContent}>
          <TextField
            label={formatMessage(messages.folderNameLabel)}
            aria-label={formatMessage(messages.folderNameLabel)}
            value={folderName}
            onChange={event => setFolderName(event.target.value)}
            errorMessage={errorMessage}
            hasError={hasError}
            fullWidth
          />
        </div>
      </Modal>
    </>
  );
};
